// The customizer's font helpers, out of TextEditor, RenderKeycapTexts and utils.js (backfill
// PR 24): the font picker's search, the loader of a font family's styles, and the check of a
// text for glyphs its font lacks. Since chain 10 (Oct 2026) also the font loader of the
// customizer, the legends and layout editors and the order processing (one copy each): a
// font's file fetched once through the API's link, registered with the document and cached as
// a fontkit font.
import { create } from 'fontkit';
import api from '../api'; // api.js imports utils.js, which imports this module: the cycle utils.js and api.js already had; api is used inside fetchFontStyles alone, so the import order doesn't matter (the glyph check's tests mock api for this import alone)

const normalizeFontFamilyName = (name) => {
  return name.replace(/\s+/g, '').toLowerCase();
}; // Normalize the font family name (remove spaces, convert to lowercase)

export function sortFontPreviewsBySearch(fontPreviews, fontFamilySearchQuery) {
  const normalizedSearchQuery = normalizeFontFamilyName(fontFamilySearchQuery); // First, normalize the search query for case-insensitive comparison
  const matchingFonts = fontPreviews.filter((font) =>
    normalizeFontFamilyName(font.fontFamily).includes(normalizedSearchQuery)
  ); // Separate the fontPreviews into matching and non-matching arrays
  const nonMatchingFonts = fontPreviews.filter(
    (font) =>
      !normalizeFontFamilyName(font.fontFamily).includes(normalizedSearchQuery)
  );
  return [...matchingFonts, ...nonMatchingFonts]; // Combine the matching fonts first, followed by the non-matching fonts
} // the font picker's list for a search: the families the query matches first, then the rest, each in the previews' order; two passes over the previews, cheap at the list's size, and TextEditor memoises the call on the previews and the search

export function fetchFontStyles(fontFamily, stylesCache, stylePromises) {
  if (stylesCache[fontFamily]) {
    return Promise.resolve(stylesCache[fontFamily]); // 1. the styles of this fontFamily were fetched before: answer them from the cache
  }

  if (stylePromises[fontFamily]) {
    return stylePromises[fontFamily]; // 2. a request for them is in flight: return the same promise, so every caller waiting on it gets the same styles and nothing is fetched twice
  }

  stylePromises[fontFamily] = api
    .get(`/api/fonts/${fontFamily}/all-font-styles`) // 3. if the font styles for the given fontFamily have not been fetched before, fetch them for the first time
    .then((response) => {
      stylesCache[fontFamily] = response.data; // store in cache
      delete stylePromises[fontFamily]; // clean up
      return response.data;
    })
    .catch((err) => {
      delete stylePromises[fontFamily]; // allow retry on error
      throw err;
    });

  return stylePromises[fontFamily];
} // Fetches and caches all font styles for a given font family, in the caller's caches of finished and in-flight requests (TextEditor keeps them in refs for its lifetime). Ensures each font family is only fetched once by reusing cached data or in-flight promises.

export const checkUnsupportedGlyphs = (font, text) => {
  const unsupported = [];
  [...text].forEach((char) => {
    const codePoint = char.codePointAt(0);
    const glyph = font.glyphForCodePoint(codePoint);

    if (!glyph || glyph.id === 0) {
      // If glyph.id is 0, it indicates the glyph does not exist in the font
      unsupported.push(char);
    }
  });
  return [...new Set(unsupported)]; // each missing character once, in the order first typed, as the glyph warnings list them
}; // checks the text, if it contains glyphs that are not supported by the 'selected' font (a fontkit font); every caller passes a string: the text editor its input's value, the legends editor an entry's text (checked for one), the font replacement the text or ''

export const fetchFont = async (
  cacheKey,
  apiUrl,
  fonts,
  fontPromises,
  setFonts
) => {
  if (fonts[cacheKey]) return fonts[cacheKey]; // if the font is already cached (already loaded), exit the function
  if (fontPromises[cacheKey]) return fontPromises[cacheKey]; // if the font is already being fetched/loaded at the moment, exit the function and don't make another fetch request

  const promise = (async () => {
    const { data } = await api.get(apiUrl); // Step 1: Fetch the ttfUrl from the backend API
    const { ttfUrl } = data;

    if (!ttfUrl) {
      throw new Error('No ttfUrl returned from ' + apiUrl);
    }

    const res = await fetch(ttfUrl); // Step 2: Fetch the font file (ttf) from the provided ttfUrl
    const buf = await res.arrayBuffer(); // Step 3: Convert the font response to an ArrayBuffer
    const fontkitFont = create(new Uint8Array(buf)); // Step 4: Use fontkit to load the font from the ArrayBuffer

    let weightRange = ''; // Default font-weight range
    let widthRange = ''; // Default font-stretch (width) range
    let slantRange = ''; // Default slant (italic) range

    if (Object.keys(fontkitFont.variationAxes).length > 0) {
      // if the font is a variable font:
      const axes = fontkitFont.variationAxes; // get the variation axes from the font

      const weightAxis = axes.wght;
      const widthAxis = axes.wdth;
      const slantAxis = axes.slnt;

      if (weightAxis) {
        weightRange = `${weightAxis.min} ${weightAxis.max}`; // Set font-weight range if available
      }
      if (widthAxis) {
        widthRange = `${widthAxis.min}% ${widthAxis.max}%`; // Set font-stretch range if available
      }
      if (slantAxis) {
        slantRange = `${slantAxis.min}deg ${slantAxis.max}deg`; // Set slant (italic) range if available
      }
    }

    const fontFaceOptions = {};
    if (weightRange) fontFaceOptions.weight = weightRange;
    if (widthRange) fontFaceOptions.stretch = widthRange;
    if (slantRange) fontFaceOptions.style = `oblique ${slantRange}`;

    const face = new FontFace(cacheKey, `url(${ttfUrl})`, fontFaceOptions); // Step 5: Create a FontFace with available ranges for variable fonts

    await face.load(); // Load the font face (downloading the font file from the specified source, parsing it, and preparing it for rendering)
    document.fonts.add(face); // Add font face to the document (registers the loaded font with the document’s FontFaceSet, making it available for use on the page)

    setFonts((prev) => ({ ...prev, [cacheKey]: fontkitFont })); // Step 6: Cache the loaded font to avoid refetching it later
    delete fontPromises[cacheKey]; // remove the font from the currently loading/fetching font list
    return fontkitFont;
  })().catch((err) => {
    delete fontPromises[cacheKey]; // remove the font from the currently loading/fetching font list
    throw err;
  });

  fontPromises[cacheKey] = promise;
  return promise;
}; // This function loads and caches fonts, in the caller's caches: the fonts loaded so far (a screen's state, written through setFonts) and the fetches in flight (its ref's object, written in place). It also makes sure that a font is only fetched once and if a font is already being fetched/loaded, it is not possible to request another fetch for this font
